import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Handshake, Trash2, Pencil, Loader2, Upload, Building2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, absUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { brl, TIERS, apiError } from "@/lib/format";

const empty = { tier: "MASTER_OURO", company_name: "", cnpj_cpf: "", business_sector: "", contact_name: "", contact_phone: "", contact_email: "", amount: 0, has_stand: false, stand_details: "", logo_url: "", active: true };

export default function Sponsors() {
  const { user } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/sponsors").then((r) => setList(r.data.sponsors)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async () => {
    if (!form.company_name.trim()) return toast.error("Informe a razão social");
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) || 0 };
      if (editing) { await api.patch(`/sponsors/${editing.id}`, payload); toast.success("Patrocinador atualizado!"); }
      else { await api.post("/sponsors", payload); toast.success("Patrocinador cadastrado!"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (s) => {
    try { await api.delete(`/sponsors/${s.id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/sponsors/logo", fd);
      set("logo_url", data.logo_url);
      toast.success("Logo enviado!");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(false); }
  };

  const totalByTier = (tier) => list.filter((s) => s.tier === tier).reduce((a, s) => a + (s.amount || 0), 0);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="admin-sponsors">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-brand font-bold uppercase tracking-widest text-xs">Parcerias</p>
          <h1 className="font-display text-4xl font-black uppercase">Patrocínios</h1>
        </div>
        {isSuper && (
          <Button data-testid="add-sponsor-btn" onClick={openNew} className="bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
            <Plus className="w-4 h-4 mr-1" /> Novo Patrocinador
          </Button>
        )}
      </div>

      {/* Tier summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {Object.entries(TIERS).map(([tier, meta]) => (
          <div key={tier} className={`rounded-2xl bg-surface border ${meta.ring} p-5`}>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${meta.chip}`}>{meta.label}</span>
              <Handshake className="w-5 h-5" style={{ color: meta.color }} />
            </div>
            <div className="font-display text-3xl font-black mt-3">{list.filter((s) => s.tier === tier).length}</div>
            <div className="text-sm text-slate-400">cotas • {brl(totalByTier(tier))}</div>
          </div>
        ))}
      </div>

      {/* List */}
      <div className="rounded-2xl bg-surface border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-white/10">
                <th className="px-4 py-3">Empresa</th><th className="px-4 py-3">Cota</th><th className="px-4 py-3">Valor</th>
                <th className="px-4 py-3">Stand</th><th className="px-4 py-3">Contato</th><th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} data-testid={`sponsor-row-${s.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {s.logo_view_url ? <img src={absUrl(s.logo_view_url)} alt="" className="w-9 h-9 rounded object-cover" />
                        : <div className="w-9 h-9 rounded bg-white/5 flex items-center justify-center"><Building2 className="w-4 h-4 text-slate-500" /></div>}
                      <div><div className="font-semibold">{s.company_name}</div><div className="text-xs text-slate-500">{s.business_sector}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIERS[s.tier]?.chip}`}>{TIERS[s.tier]?.label}</span></td>
                  <td className="px-4 py-3 font-mono">{s.amount > 0 ? brl(s.amount) : <span className="text-slate-500 text-xs">Permuta/Brinde</span>}</td>
                  <td className="px-4 py-3">{s.has_stand ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{s.contact_name}<br />{s.contact_phone}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {isSuper && <>
                        <Button data-testid={`edit-sponsor-${s.id}`} size="icon" variant="ghost" className="h-8 w-8 hover:bg-white/10" onClick={() => openEdit(s)}><Pencil className="w-4 h-4" /></Button>
                        <Button data-testid={`del-sponsor-${s.id}`} size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-500/10 text-red-400" onClick={() => del(s)}><Trash2 className="w-4 h-4" /></Button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400">Nenhum patrocinador</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="sponsor-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editing ? "Editar" : "Novo"} Patrocinador</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Razão Social *</Label>
                <Input data-testid="sponsor-name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Cota</Label>
                <Select value={form.tier} onValueChange={(v) => set("tier", v)}>
                  <SelectTrigger data-testid="sponsor-tier" className="bg-ink border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">{Object.entries(TIERS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">CNPJ / CPF</Label><Input data-testid="sponsor-doc" value={form.cnpj_cpf} onChange={(e) => set("cnpj_cpf", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Ramo de Atividade</Label><Input value={form.business_sector} onChange={(e) => set("business_sector", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">Responsável</Label><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Telefone/WhatsApp</Label><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-slate-300">E-mail</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
              <div><Label className="text-slate-300">Valor investido (R$)</Label><Input data-testid="sponsor-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="bg-ink border-white/10 mt-1" /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl bg-ink border border-white/10 p-3">
              <Label className="text-slate-300">Inclui Stand no evento?</Label>
              <Switch data-testid="sponsor-stand" checked={form.has_stand} onCheckedChange={(v) => set("has_stand", v)} />
            </div>
            {form.has_stand && <Textarea value={form.stand_details} onChange={(e) => set("stand_details", e.target.value)} className="bg-ink border-white/10" placeholder="Metragem e especificações do stand" />}
            <div>
              <Label className="text-slate-300">Logotipo</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.logo_url && <img src={absUrl(`/api/files/${form.logo_url}`)} alt="" className="w-12 h-12 rounded object-cover border border-white/10" />}
                <label className="inline-flex">
                  <input type="file" accept="image/*" className="hidden" data-testid="sponsor-logo-input" onChange={(e) => uploadLogo(e.target.files[0])} />
                  <span className="inline-flex items-center px-4 h-10 rounded-md bg-white/5 border border-white/10 hover:bg-white/10 cursor-pointer text-sm font-bold">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Enviar logo</>}
                  </span>
                </label>
              </div>
            </div>
            <Button data-testid="sponsor-save-btn" onClick={save} disabled={saving} className="w-full bg-brand hover:bg-brand-hover font-bold uppercase tracking-wide">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
